import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Tarjeta } from '../componentes/ui/basicos';
import { Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';

interface Registro {
  readonly id: string;
  readonly ocurridoEn: string;
  readonly entidad: string;
  readonly entidadId: string | null;
  readonly accion: string;
  readonly correoDelUsuario: string | null;
  readonly nombreDelUsuario: string | null;
}

interface Opcion {
  readonly valor: string;
  readonly clave: string;
}

interface Opciones {
  readonly entidades: readonly Opcion[];
  readonly acciones: readonly Opcion[];
}

/**
 * Visor de la bitácora.
 *
 * Se pagina con "ver más" en lugar de números de página: la consulta usa cursor, así que
 * saltar a la página cuarenta no es algo que la base pueda hacer barato, y en una bitácora
 * tampoco es algo que alguien quiera hacer. Se busca con filtros, no paginando.
 */
export function Auditoria(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const [entidad, setEntidad] = useState('');
  const [accion, setAccion] = useState('');
  const [paginas, setPaginas] = useState<string[]>(['']);

  const opciones = useQuery({
    queryKey: ['auditoria', 'opciones'],
    queryFn: () => pedir<Opciones>('/auditoria/opciones'),
  });

  const cursorActual = paginas.at(-1) ?? '';
  const consulta = useQuery({
    queryKey: ['auditoria', entidad, accion, cursorActual],
    queryFn: () => {
      const parametros = new URLSearchParams();
      if (entidad !== '') parametros.set('entidad', entidad);
      if (accion !== '') parametros.set('accion', accion);
      if (cursorActual !== '') parametros.set('cursor', cursorActual);
      return pedir<{ registros: Registro[]; cursor: string | null }>(
        `/auditoria?${parametros.toString()}`,
      );
    },
  });

  const formatoDeMomento = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'medium',
  });

  const columnas: Columna<Registro>[] = [
    {
      clave: 'momento',
      titulo: t('auditoria:columnas.momento'),
      contenido: (fila) => (
        <span className="numero whitespace-nowrap">
          {formatoDeMomento.format(new Date(fila.ocurridoEn))}
        </span>
      ),
    },
    {
      clave: 'quien',
      titulo: t('auditoria:columnas.quien'),
      contenido: (fila) => (
        <span title={fila.correoDelUsuario ?? undefined}>
          {fila.nombreDelUsuario ?? fila.correoDelUsuario ?? '—'}
        </span>
      ),
    },
    {
      clave: 'que',
      titulo: t('auditoria:columnas.que'),
      contenido: (fila) => t(`auditoria:entidades.${fila.entidad}`, { defaultValue: fila.entidad }),
    },
    {
      clave: 'accion',
      titulo: t('auditoria:columnas.accion'),
      contenido: (fila) => t(`auditoria:acciones.${fila.accion}`, { defaultValue: fila.accion }),
    },
  ];

  const reiniciarPaginado = (): void => {
    setPaginas(['']);
  };

  return (
    <>
      <Encabezado titulo={t('auditoria:titulo')} descripcion={t('auditoria:descripcion')} />

      <Tarjeta>
        <div className="mb-4 flex flex-wrap gap-3">
          <Selector
            etiqueta={t('auditoria:columnas.que')}
            value={entidad}
            onChange={(evento) => {
              setEntidad(evento.target.value);
              reiniciarPaginado();
            }}
          >
            <option value="">{t('auditoria:filtros.todas')}</option>
            {(opciones.data?.entidades ?? []).map((opcion) => (
              <option key={opcion.valor} value={opcion.valor}>
                {t(opcion.clave, { defaultValue: opcion.valor })}
              </option>
            ))}
          </Selector>

          <Selector
            etiqueta={t('auditoria:columnas.accion')}
            value={accion}
            onChange={(evento) => {
              setAccion(evento.target.value);
              reiniciarPaginado();
            }}
          >
            <option value="">{t('auditoria:filtros.todas')}</option>
            {(opciones.data?.acciones ?? []).map((opcion) => (
              <option key={opcion.valor} value={opcion.valor}>
                {t(opcion.clave, { defaultValue: opcion.valor })}
              </option>
            ))}
          </Selector>
        </div>

        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <>
            <Tabla
              columnas={columnas}
              filas={consulta.data.registros}
              claveDeFila={(fila) => fila.id}
              vacio={<Vacio />}
            />
            {consulta.data.cursor === null ? null : (
              <div className="mt-3 flex justify-center">
                <Boton
                  onClick={() => {
                    setPaginas((previas) => [...previas, consulta.data.cursor ?? '']);
                  }}
                >
                  {t('auditoria:masResultados')}
                </Boton>
              </div>
            )}
          </>
        ) : null}
      </Tarjeta>
    </>
  );
}
